import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { teamService, ITeamDetail } from './service/team.service';
import useAuth from '../auth/feature/auth';
import { InviteModal } from '../common/components/invite-modal.component';
import { UpgradeModal } from '../common/components/upgrade-modal.component';
import { TeamInviteTracker } from './components/team-invite-tracker.component';
import { useTeamSocket } from './hooks/use-team-socket';

export const TeamAdminPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copied, setCopied] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState<string | null>(null);
  const [editingPassword, setEditingPassword] = React.useState(false);
  const [passwordInput, setPasswordInput] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [openMenuId, setOpenMenuId] = React.useState<number | null>(null);
  const [editingName, setEditingName] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const menuRef = React.useRef<HTMLDivElement>(null);
  useTeamSocket(id);

  // Close menu on outside click
  React.useEffect(() => {
    if (openMenuId === null) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  const { data: team, isLoading } = useQuery<ITeamDetail | void>({
    queryKey: ['team', id],
    queryFn: () => teamService.getTeam(+id!),
    enabled: !!id
  });

  const { mutate: renameTeam, isPending: renamePending } = useMutation({
    mutationFn: (name: string) => teamService.updateTeam(+id!, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      queryClient.invalidateQueries({ queryKey: ['my-teams'] });
      setEditingName(false);
      toast.success(t('teams.teamRenamed'));
    },
    onError: () => toast.error(t('teams.renameFailed'))
  });

  const { mutate: removeMember } = useMutation({
    mutationFn: (userId: number) => teamService.removeMember(+id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      toast.success(t('teams.memberRemoved'));
    }
  });

  const { mutate: changeRole } = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) =>
      teamService.changeMemberRole(+id!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', id] });
      toast.success(t('teams.roleUpdated'));
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      toast.error(msg || t('teams.roleChangeFailed'));
    }
  });

  const { mutate: deleteTeam } = useMutation({
    mutationFn: () => teamService.deleteTeam(+id!),
    onSuccess: () => {
      // Remove the team from cache directly so the list updates instantly
      queryClient.setQueryData(['my-teams'], (old: any) => {
        if (Array.isArray(old)) return old.filter((t: any) => t.id !== +id!);
        return old;
      });
      queryClient.removeQueries({ queryKey: ['team', id] });
      toast.success(t('teams.teamDeleted'));
      navigate('/teams');
    }
  });

  const { data: teamPassword } = useQuery({
    queryKey: ['team-password', id],
    queryFn: () => teamService.getTeamPassword(+id!),
    enabled: !!id
  });

  const { mutate: saveTeamPassword } = useMutation({
    mutationFn: (pw: string) => teamService.updateTeamPassword(+id!, pw || undefined),
    onSuccess: () => {
      setEditingPassword(false);
      queryClient.invalidateQueries({ queryKey: ['team-password', id] });
      toast.success(t('teams.teamPasswordUpdated'));
    }
  });

  const copyInvite = () => {
    if (!team) return;
    navigator.clipboard.writeText(
      `${window.location.origin}/team/join/${team.inviteCode}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading || !team) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] dark:border-[#C8C8CB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-[600px] mx-auto py-[32px] md:py-[48px] px-[16px]">
      <Link to={`/team/${id}`} className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white transition-colors mb-[8px]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        {team.name}
      </Link>
      <div className="mb-[24px]">
        {editingName ? (
          <div className="flex gap-[8px] mb-[12px]">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="flex-1 h-[44px] px-[16px] text-[20px] font-display font-bold text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && nameInput.trim()) renameTeam(nameInput.trim()); if (e.key === 'Escape') setEditingName(false); }}
            />
            <button
              type="button"
              onClick={() => { if (nameInput.trim()) renameTeam(nameInput.trim()); }}
              disabled={renamePending || !nameInput.trim()}
              className="h-[44px] px-[16px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => setEditingName(false)}
              className="h-[44px] px-[16px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-[10px] mb-[12px]">
            <h1 className="font-display text-[24px] md:text-[28px] font-bold text-base-black dark:text-white">
              {t('teams.manage', { name: team.name })}
            </h1>
            <button
              type="button"
              onClick={() => { setNameInput(team.name); setEditingName(true); }}
              className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors flex-shrink-0"
              title={t('teams.renameTeam')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => deleteTeam()}
          className="h-[36px] px-[14px] rounded-[32px] border border-red-500 bg-white dark:bg-transparent font-body text-[13px] font-medium text-red-500 hover:bg-red-500 hover:text-white transition-all"
        >
          {t('teams.deleteTeam')}
        </button>
      </div>

      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)]">

      <div className="mb-[24px] flex items-center gap-[8px] flex-wrap">
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="h-[36px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all flex items-center gap-[6px]"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
          </svg>
          {t('teams.inviteMembers')}
        </button>
        <TeamInviteTracker
          teamId={+id!}
          isPro={user?.isPro || false}
          onUpgrade={() => setUpgradeMessage(t('review.inviteTrackerProRequired'))}
        />
      </div>

      <div className="mb-[24px]">
        <p className="font-body text-[12px] font-semibold text-gray-900 dark:text-[#6B6B6F] uppercase tracking-wider mb-[6px]">
          {t('teams.joinPassword')}
        </p>
        {editingPassword ? (
          <div className="flex gap-[6px]">
            <div className="relative flex-1">
              <input
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={t('review.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                className="w-full h-[36px] px-[12px] pr-[36px] text-[13px] font-body bg-white dark:bg-[#28282B] dark:text-white border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[18px] dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[#818185] hover:text-base-black dark:hover:text-white transition-colors"
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>
            <button type="button" onClick={() => saveTeamPassword(passwordInput)} className="h-[36px] px-[12px] rounded-[18px] bg-[#1D1D1E] dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[12px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all">{t('common.save')}</button>
            <button type="button" onClick={() => setEditingPassword(false)} className="h-[36px] px-[12px] rounded-[18px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[12px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all">{t('common.cancel')}</button>
          </div>
        ) : (
          <div className="flex items-center gap-[8px]">
            <span className="font-body text-[13px] text-gray-900 dark:text-[#A0A0A3]">
              {(teamPassword as any)?.password ? t('teams.passwordSet') : t('teams.noPassword')}
            </span>
            <button
              type="button"
              onClick={() => { setPasswordInput((teamPassword as any)?.password || ''); setEditingPassword(true); }}
              className="font-body text-[13px] text-[#4A4A4C] dark:text-[#A0A0A3] hover:text-[#1D1D1E] dark:hover:text-white transition-colors"
            >
              {(teamPassword as any)?.password ? t('review.changePassword') : t('review.setPassword')}
            </button>
          </div>
        )}
      </div>

      <p className="font-body text-[12px] font-semibold text-gray-900 dark:text-[#6B6B6F] uppercase tracking-wider mb-[8px]">
        {t('teams.membersCount', { count: team.members.length })}
      </p>

      <div className="flex flex-col gap-[6px]">
        {team.members.map((member) => (
          <div
            key={member.id}
            className="flex items-center gap-[12px] p-[12px_16px] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.06)] bg-white dark:bg-[#28282B] shadow-[0_1px_3px_rgba(0,0,0,0.07)]"
          >
            <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] text-[12px] font-semibold flex-shrink-0">
              {(member.displayName || member.email).slice(0, 2).toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-body text-[14px] font-semibold text-[#111827] dark:text-white">
                {member.displayName || member.email.split('@')[0]}
              </p>
              <p className="font-body text-[12px] text-gray-900 dark:text-[#A0A0A3] break-all">
                {member.email}
              </p>
              <p className="font-body text-[11px] text-gray-700 dark:text-[#6B6B6F]">
                {t('teams.joined', { date: new Date(member.joinedAt).toLocaleDateString() })}
              </p>
            </div>
            <span className={`text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] uppercase flex-shrink-0 ${
              member.role === 'admin' ? 'bg-[#1D1D1E] text-white' : 'bg-[#EFEFF1] dark:bg-[#333336] text-[#4A4A4C] dark:text-[#A0A0A3]'
            }`}>
              {member.role === 'admin' ? t('common.admin') : t('common.memberRole')}
            </span>
            {member.userId !== user?.id && (
              <div className="relative flex-shrink-0" ref={openMenuId === member.id ? menuRef : undefined}>
                <button
                  type="button"
                  onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                  className={`w-[32px] h-[32px] flex items-center justify-center rounded-[8px] transition-colors ${
                    openMenuId === member.id ? 'bg-[#EFEFF1] dark:bg-[#333336]' : 'hover:bg-[#EFEFF1] dark:hover:bg-[#333336] active:bg-[#E5E5ED] dark:active:bg-[#3A3A3D]'
                  }`}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="#818185">
                    <circle cx="8" cy="3" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="8" cy="13" r="1.5" />
                  </svg>
                </button>
                {openMenuId === member.id && (
                  <div className="absolute right-0 top-[36px] z-20 min-w-[180px] bg-white dark:bg-[#28282B] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.10)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] py-[6px] animate-[fadeInMenu_150ms_ease-out]">
                    <p className="font-body text-[11px] font-semibold text-[#818185] dark:text-[#6B6B6F] uppercase tracking-wider px-[14px] pt-[8px] pb-[4px]">
                      {t('teams.role')}
                    </p>
                    {['member', 'admin'].map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => {
                          if (role !== member.role) {
                            changeRole({ userId: member.userId, role });
                          }
                          setOpenMenuId(null);
                        }}
                        className={`w-full flex items-center justify-between px-[14px] py-[8px] font-body text-[13px] text-[#111827] dark:text-white hover:bg-[#F5F5F6] dark:hover:bg-[#333336] transition-colors ${
                          role === member.role ? 'font-semibold' : ''
                        }`}
                      >
                        <span className="capitalize">{role === 'admin' ? t('common.admin') : t('common.memberRole')}</span>
                        {role === member.role && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D1D1E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    ))}
                    <div className="border-t border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.08)] my-[4px]" />
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenuId(null);
                        removeMember(member.userId);
                      }}
                      className="w-full text-left px-[14px] py-[8px] font-body text-[13px] text-[#D8210C] hover:bg-[#FEF2F2] dark:hover:bg-[#D8210C]/10 transition-colors"
                    >
                      {t('teams.removeMember')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      </div>

      {team && (
        <InviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          title={t('inviteModal.inviteToTeam')}
          copyLabel={t('inviteModal.teamInviteLink')}
          copyUrl={`${window.location.origin}/team/join/${team.inviteCode}`}
          isPro={user?.isPro || false}
          onUpgrade={() => setUpgradeMessage(t('review.inviteTrackerProRequired'))}
          onSendInvites={async (emails) => {
            await teamService.sendInvites(team.id, emails);
            queryClient.invalidateQueries({ queryKey: ['team-invites', +id!] });
            toast.success(`Invited ${emails.length} ${emails.length === 1 ? 'person' : 'people'} to ${team.name}`);
          }}
        />
      )}

      {upgradeMessage && (
        <UpgradeModal message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />
      )}
    </div>
  );
};
